/**
 * custom-shipping-ladder.js
 *
 * Client half of sections/custom-shipping-ladder.liquid — the shipping-discount
 * top bar. Liquid renders the correct state for the cart as it stands on page
 * load; this module keeps that state true while the customer shops without
 * reloading, and runs the celebration when a tier lands.
 *
 * MEASURED AGAINST items_subtotal_price, NOT total_price. The shipping
 * discounts are automatic *order* discounts, so a landed tier reduces the cart
 * total — measuring against the total would drop the customer back below the
 * threshold they just cleared. /cart.js exposes both; we read
 * `items_subtotal_price`, matching the Liquid side.
 *
 * WHY /cart.js AND NOT THE EVENT PAYLOAD: the standard cartLinesUpdate result
 * carries `cost.totalAmount` (post-discount) and per-line totals whose discount
 * allocation is not guaranteed to exclude order-level discounts. The Ajax cart
 * is the same object Liquid rendered from, so the two halves can never disagree.
 *
 * PROGRESS IS LADDER-WIDE, NOT PER-SEGMENT: `pct` is
 * ((tierIndex + 1) + fractionOfCurrentSegment) / tierCount, which puts
 * milestone N exactly where the fill stops when tier N lands. Keep this in step
 * with the identical expression in the Liquid — the two must agree or the bar
 * jumps on the first cart update after load.
 */

import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** How long the "just unlocked" message holds before settling into "next tier". */
const CELEBRATION_MS = 3400;

/** Crossfade duration for the message swap — matches the CSS transition. */
const SWAP_MS = 180;

const CONFETTI_COUNT = 16;

/**
 * @typedef {object} LadderState
 * @property {number} index - Highest tier cleared, -1 when none.
 * @property {boolean} atMax - True once the top tier is cleared.
 * @property {number} pct - Progress across the whole ladder, 0-100.
 * @property {number} remaining - Minor units still needed for the next tier.
 * @property {number} current - Discount already earned, in minor units.
 * @property {number} next - Next tier's discount, in minor units.
 * @property {number} threshold - Next tier's order value, in minor units.
 * @property {number} max - Largest discount on the ladder, in minor units.
 */

class ShippingLadder extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #celebrationTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #swapTimer = null;
  /** @type {number} */
  #subtotal = 0;
  /** @type {number} */
  #tierIndex = -1;
  /** Private-mode fallback for #celebrationFloor when sessionStorage throws. */
  #celebratedTier = -1;
  /** Monotonic token so a slow /cart.js can never overwrite a newer one. */
  #requestId = 0;

  connectedCallback() {
    this.config = this.#readConfig();
    if (!this.config?.tiers?.length) return;

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.moneyFormatWhole = this.#wholeUnitFormat(this.moneyFormat);
    this.currency = this.dataset.currency || 'AUD';
    this.storageKey = this.dataset.storageKey || 'phaser:shipping-ladder';
    this.version = this.dataset.version || '';

    this.textEl = this.querySelector('[data-csl-text]');
    this.textShortEl = this.querySelector('[data-csl-text-short]');
    this.fillEl = this.querySelector('[data-csl-fill]');
    this.milestoneEls = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-csl-ms]')]);
    this.confettiEl = this.querySelector('[data-csl-confetti]');

    this.#subtotal = Number(this.config.subtotal) || 0;
    this.#tierIndex = Number(this.dataset.tier ?? -1);

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelector('[data-csl-close]')?.addEventListener('click', this.#onClose, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    // A tier reached on a previous page (added from a PDP, then navigated)
    // still deserves its moment — but only once per tier, per session.
    this.#maybeCelebrateOnLoad();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.#celebrationTimer) clearTimeout(this.#celebrationTimer);
    if (this.#swapTimer) clearTimeout(this.#swapTimer);
  }

  /** @returns {object | null} */
  #readConfig() {
    const el = this.querySelector('script[data-csl-config]');
    if (!el?.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[Phaser] Shipping ladder config is not valid JSON', error);
      return null;
    }
  }

  // ── Cart tracking ────────────────────────────────────────────────────────

  /** @param {CustomEvent & { promise?: Promise<unknown> }} event */
  #onCartUpdate = (event) => {
    const refresh = () => this.#refreshFromCart();
    if (event.promise) {
      event.promise.then(refresh).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[Phaser] Shipping ladder skipped a cart update:', error);
      });
    } else {
      refresh();
    }
  };

  async #refreshFromCart() {
    // Hammering the quantity stepper fires overlapping reads; the last one
    // started is the only one allowed to land, or the rail can settle on a
    // subtotal the cart has already moved past.
    const request = ++this.#requestId;

    try {
      const base = (typeof Theme !== 'undefined' && Theme?.routes?.cart_url) || '/cart';
      const response = await fetch(`${base}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#abort?.signal,
      });
      if (!response.ok) throw new Error(`${base}.js ${response.status}`);
      const cart = await response.json();
      if (request !== this.#requestId) return;

      // A currency switch mid-session invalidates the fixed-AUD thresholds the
      // same way it does server-side; drop the bar rather than lie about them.
      if (cart.currency && this.currency && cart.currency !== this.currency) {
        this.hidden = true;
        return;
      }

      this.#apply(Number(cart.items_subtotal_price) || 0);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[Phaser] Shipping ladder cart read failed:', error);
    }
  }

  // ── State ────────────────────────────────────────────────────────────────

  /**
   * @param {number} subtotal - Cart subtotal in minor units.
   * @returns {LadderState}
   */
  #resolve(subtotal) {
    const tiers = this.config.tiers;
    const count = tiers.length;

    let index = -1;
    for (let i = 0; i < count; i++) {
      // >=, matching Shopify's greaterThanOrEqualToSubtotal: $99.00 exactly
      // already earns the $5.
      if (subtotal >= tiers[i].t) index = i;
    }

    const nextIndex = index + 1;
    const atMax = nextIndex >= count;
    const segStart = index >= 0 ? tiers[index].t : 0;
    const current = index >= 0 ? tiers[index].d : 0;
    const max = tiers[count - 1].d;

    if (atMax) {
      return { index, atMax, pct: 100, remaining: 0, current, next: 0, threshold: 0, max };
    }

    const threshold = tiers[nextIndex].t;
    const span = threshold - segStart;
    const fraction = span > 0 ? Math.min(1, Math.max(0, (subtotal - segStart) / span)) : 0;
    const pct = ((nextIndex + fraction) / count) * 100;

    return {
      index,
      atMax,
      pct: Math.round(pct * 100) / 100,
      remaining: Math.max(0, threshold - subtotal),
      current,
      next: tiers[nextIndex].d,
      threshold,
      max,
    };
  }

  /** @param {number} subtotal */
  #apply(subtotal) {
    const state = this.#resolve(subtotal);
    // A tier is worth celebrating once per session. Nudging a quantity up and
    // down across $99 should not fire confetti on every pass, and dropping a
    // tier then regaining it is not news.
    const floor = this.#celebrationFloor();
    const unlocked = state.index > this.#tierIndex && state.index > floor;

    this.#subtotal = subtotal;
    this.#tierIndex = state.index;
    this.dataset.tier = String(state.index);

    if (unlocked && this.hidden && this.config.reopenOnUnlock) this.#reopen();

    this.#paint(state, unlocked ? state.index : null);

    if (unlocked && this.config.celebrate && !this.hidden) {
      this.#rememberCelebration(state.index);
      this.#celebrate(state);
      return;
    }

    // Removing items mid-celebration would otherwise leave "Nice! $8 off
    // unlocked" on screen for seconds after the rail has already retreated.
    if (this.hasAttribute('data-celebrating') && state.index < floor) this.#endCelebration();
    this.#announce(state);
  }

  /**
   * Rail and milestones. The message is handled separately because the
   * celebration owns it for a few seconds.
   *
   * @param {LadderState} state
   * @param {number | null} justEarned
   */
  #paint(state, justEarned) {
    if (this.fillEl) this.fillEl.style.width = `${state.pct}%`;
    if (state.atMax) this.dataset.state = 'max';
    else delete this.dataset.state;

    this.milestoneEls.forEach((ms, i) => {
      ms.dataset.msState = i <= state.index ? 'done' : i === state.index + 1 ? 'next' : 'later';
      if (justEarned !== null && i === justEarned) ms.setAttribute('data-ms-just-earned', '');
      else ms.removeAttribute('data-ms-just-earned');
    });
  }

  // ── Copy ─────────────────────────────────────────────────────────────────

  /**
   * Liquid renders every amount through `money_without_trailing_zeros`, so the
   * client must drop `.00` too or the message flips from "$5 off" to "$5.00
   * off" the moment the cart changes. Swapping the placeholder for its
   * no-decimals twin keeps separators and symbol placement in the shop's hands.
   *
   * @param {number} cents
   */
  #money(cents) {
    const format = cents % 100 === 0 ? this.moneyFormatWhole : this.moneyFormat;
    return formatMoney(cents, format, this.currency);
  }

  /**
   * @param {string} format
   * @returns {string}
   */
  #wholeUnitFormat(format) {
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, '{{amount_no_decimals_with_space_separator}}')
      .replace(
        /\{\{\s*amount_with_period_and_space_separator\s*\}\}/g,
        '{{amount_no_decimals_with_space_separator}}'
      );
  }

  /**
   * @param {LadderState} state
   * @param {boolean} [celebrating]
   * @param {boolean} [short] - Narrow-screen wording; falls back to the full copy.
   * @returns {string}
   */
  #composeMessage(state, celebrating = false, short = false) {
    const copy = this.config.copy || {};
    let template;

    // At the top of the ladder there is no "add X more" half to celebrate with,
    // and the top-tier copy is already a congratulation.
    if (celebrating && !state.atMax) template = (short && copy.unlockedShort) || copy.unlocked;
    else if (state.atMax) template = (short && copy.maxShort) || copy.max;
    else if (state.index >= 0) template = (short && copy.nextShort) || copy.next;
    else if (this.#subtotal > 0) template = (short && copy.startShort) || copy.start;
    else template = (short && copy.emptyShort) || copy.empty;

    if (!template) return '';

    const tokens = {
      amount: this.#money(state.remaining),
      discount: this.#money(state.next),
      threshold: this.#money(state.threshold),
      current: this.#money(state.current),
      max: this.#money(state.max),
    };

    // [square] brackets, not {{ curly }} — Shopify treats {{ }} inside a setting
    // value as a Theme Editor dynamic source and rejects the section group.
    return template.replace(/\[(amount|discount|threshold|current|max)\]/g, (_, key) => tokens[key] ?? '');
  }

  /**
   * @param {LadderState} state
   * @param {boolean} [celebrating]
   */
  #announce(state, celebrating = false) {
    this.#setMessage(
      this.#composeMessage(state, celebrating, false),
      this.#composeMessage(state, celebrating, true)
    );
  }

  /**
   * Both wordings are always written; CSS decides which one is on screen, so
   * a resize never leaves the other variant stale.
   *
   * @param {string} text
   * @param {string} short
   */
  #setMessage(text, short) {
    if (!this.textEl) return;
    if (this.textEl.textContent === text && this.textShortEl?.textContent === short) return;

    // Crossfade rather than a hard swap — the amount changes on every cart
    // tweak and a blink at 12px reads as a glitch.
    if (this.#swapTimer) clearTimeout(this.#swapTimer);
    this.setAttribute('data-swapping', '');
    this.#swapTimer = setTimeout(() => {
      if (this.textEl) this.textEl.textContent = text;
      if (this.textShortEl) this.textShortEl.textContent = short;
      this.removeAttribute('data-swapping');
    }, SWAP_MS);
  }

  // ── Celebration ──────────────────────────────────────────────────────────

  /** @param {LadderState} state */
  #celebrate(state) {
    if (this.#celebrationTimer) clearTimeout(this.#celebrationTimer);

    this.#announce(state, true);
    this.#spawnConfetti();
    this.setAttribute('data-celebrating', '');

    this.#celebrationTimer = setTimeout(() => {
      this.#endCelebration();
      // Recompute rather than reuse `state`: the customer may have kept adding
      // while the celebration was on screen.
      this.#announce(this.#resolve(this.#subtotal));
    }, CELEBRATION_MS);
  }

  #endCelebration() {
    if (this.#celebrationTimer) clearTimeout(this.#celebrationTimer);
    this.#celebrationTimer = null;
    this.removeAttribute('data-celebrating');
    this.milestoneEls.forEach((ms) => ms.removeAttribute('data-ms-just-earned'));
  }

  #spawnConfetti() {
    if (!this.confettiEl) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const accent = getComputedStyle(this).getPropertyValue('--csl-accent').trim() || '#ED1C24';
    const colours = [accent, accent, accent, 'currentColor', '#f5b301'];

    this.confettiEl.textContent = '';
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < CONFETTI_COUNT; i++) {
      const piece = document.createElement('span');
      // Deterministic spread with jittered flight: an even rake of launch
      // points reads as a burst, fully random ones read as noise.
      piece.style.setProperty('--x', `${(i / (CONFETTI_COUNT - 1)) * 100}%`);
      piece.style.setProperty('--dx', `${Math.round((Math.random() - 0.5) * 80)}px`);
      piece.style.setProperty('--dy', `${-10 - Math.round(Math.random() * 20)}px`);
      piece.style.setProperty('--r', `${Math.round(Math.random() * 360)}deg`);
      piece.style.setProperty('--d', `${Math.round(Math.random() * 200)}ms`);
      piece.style.setProperty('--c', colours[i % colours.length]);
      fragment.appendChild(piece);
    }

    this.confettiEl.appendChild(fragment);
  }

  /**
   * Session memory so a celebration fires once per tier, not on every page of
   * the visit that follows it.
   */
  #maybeCelebrateOnLoad() {
    if (!this.config.celebrate || this.hidden || this.#tierIndex < 0) return;
    if (this.#tierIndex <= this.#celebrationFloor()) return;

    this.#rememberCelebration(this.#tierIndex);
    const state = this.#resolve(this.#subtotal);
    this.#paint(state, state.index);
    this.#celebrate(state);
  }

  /**
   * Highest tier already celebrated. Read from sessionStorage every time rather
   * than cached, so the store stays the single source of truth; the in-memory
   * value is only a fallback for private mode, where a repeated celebration is
   * a better failure than a missing one.
   *
   * @returns {number}
   */
  #celebrationFloor() {
    try {
      const raw = window.sessionStorage.getItem(`${this.storageKey}:celebrated`);
      // An absent key means "nothing celebrated yet" — it must not fall back to
      // the in-memory value, or a cleared session could never celebrate again.
      return raw === null ? -1 : Number(raw);
    } catch {
      return this.#celebratedTier;
    }
  }

  /** @param {number} index */
  #rememberCelebration(index) {
    this.#celebratedTier = index;
    try {
      window.sessionStorage.setItem(`${this.storageKey}:celebrated`, String(index));
    } catch {
      /* no-op */
    }
  }

  // ── Dismissal ────────────────────────────────────────────────────────────

  #onClose = () => {
    const height = this.offsetHeight;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify({ v: this.version, tier: this.#tierIndex, at: Date.now() })
      );
    } catch {
      /* private mode — the bar just comes back next load */
    }

    this.dataset.dismissed = '1';

    if (reduced || typeof this.animate !== 'function') {
      this.hidden = true;
      return;
    }

    // Collapsing rather than snapping: header.js observes every header-group
    // child, so --header-group-height follows the animation instead of jumping.
    this.style.overflow = 'hidden';
    const animation = this.animate(
      [
        { height: `${height}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 260, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );

    animation.finished
      .catch(() => {})
      .finally(() => {
        this.hidden = true;
        this.style.overflow = '';
      });
  };

  #reopen() {
    this.hidden = false;
    delete this.dataset.dismissed;
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch {
      /* no-op */
    }
  }
}

if (!customElements.get('shipping-ladder')) {
  customElements.define('shipping-ladder', ShippingLadder);
}
